import { Clock } from '@/modules/working_hours/domain/Clock';

export function formatTimestamp(d: string | Date): string {
  let date: Date;
  if (typeof d === "string")
    date = new Date(d);
  else
    date = d
  return date.toLocaleString("es-ES", {
    day: "2-digit",
    month: "short",
    hour: "2-digit",
    minute: "2-digit",
    hour12: false,
  });
}

export function formatClockType(type: string): string {
  if (type === "in" || type === "clock_in") return "Entrada";
  if (type === "out" || type === "clock_out") return "Salida";
  if (type === "pause") return "Pausa";
  if (type === "continue") return "Continuar";
  return type;
}

export function formatDate(dateStr) { 
  const date = new Date(dateStr);
  date.setHours(date.getHours() - 0);
     
  const options = {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
    hour12: false,
  } as const;
  return date.toLocaleString('es-ES', options);
}

export function formatTime(seconds: number): string { 
    const h = Math.floor(seconds / 3600).toString().padStart(2, '0');
    const m = Math.floor((seconds % 3600) / 60).toString().padStart(2, '0');
    const s = (seconds % 60).toString().padStart(2, '0');
    return `${h}:${m}:${s}`;
  }


// Filtra un array de clocks para quedarse solo con los de hoy (hora local).
export function filterTodayClocks(clocks: Clock[]): Clock[] {
  const now = new Date();
  const todayStart = new Date(now.getFullYear(), now.getMonth(), now.getDate()).getTime();
  const todayEnd = todayStart + 24 * 60 * 60 * 1000;

  return clocks.filter(clock => {
    const t = new Date(clock.timestamp).getTime();
    return t >= todayStart && t < todayEnd;
  });
}

export function get_day_secs(clocks: Clock[]): number {
  let totalSecs = 0;
  if (clocks.length === 0) return totalSecs;

  const now = new Date();
  const sorted = [...clocks]
    .filter(c => new Date(c.timestamp).getTime() <= now.getTime())
    .sort((a, b) => new Date(a.timestamp).getTime() - new Date(b.timestamp).getTime());
  if (sorted.length === 0) return totalSecs;
  let lastStart: Date | null = null;

  for (const clock of sorted) {
    const time = new Date(clock.timestamp);
    if (clock.type === 'in' || clock.type === 'continue') {
      lastStart = time;
    } else if ((clock.type === 'out' || clock.type === 'pause') && lastStart) {
      totalSecs += (time.getTime() - lastStart.getTime()) / 1000;
      lastStart = null;
    }
  }

  const last = sorted[sorted.length - 1];
  if ((last.type === 'in' || last.type === 'continue') && lastStart) {
    totalSecs += (now.getTime() - lastStart.getTime()) / 1000;
  }

  return Math.round(totalSecs);
}

export interface ProgressSegment {
  type: 'work' | 'pause';
  width: number;
  left: number;
  duration: number;
  active?: boolean;
}

export function buildProgressSegments(clocks: Clock[], expectedSecs: number): ProgressSegment[] {
  const segments: ProgressSegment[] = [];
  const now = new Date();
  const sorted = [...clocks]
    .filter(c => new Date(c.timestamp).getTime() <= now.getTime())
    .sort((a, b) => new Date(a.timestamp).getTime() - new Date(b.timestamp).getTime());
  if (sorted.length === 0) return segments;

  let lastStart: Date | null = null;
  let currentOffset = 0;

  for (let i = 0; i < sorted.length; i++) {
    const clock = sorted[i];
    const time = new Date(clock.timestamp);

    if (clock.type === 'in' || clock.type === 'continue') {
      lastStart = time;
    } else if (lastStart) {
      const duration = (time.getTime() - lastStart.getTime()) / 1000;
      const segmentWidth = (duration / expectedSecs) * 100;

      if (clock.type === 'pause') {
        segments.push({
          type: 'work',
          width: Math.min(segmentWidth, 100 - currentOffset),
          left: currentOffset,
          duration
        });
        currentOffset += segmentWidth;
        lastStart = time;
      } else if (clock.type === 'out') {
        segments.push({
          type: 'work',
          width: Math.min(segmentWidth, 100 - currentOffset),
          left: currentOffset,
          duration
        });
        currentOffset += segmentWidth;
        lastStart = null;
      }
    }

    if (clock.type === 'continue' && i > 0 && sorted[i - 1].type === 'pause') {
      const pauseStart = new Date(sorted[i - 1].timestamp);
      const pauseDuration = (time.getTime() - pauseStart.getTime()) / 1000;
      const pauseWidth = (pauseDuration / expectedSecs) * 100;

      segments.push({
        type: 'pause',
        width: Math.min(pauseWidth, 100 - currentOffset),
        left: currentOffset,
        duration: pauseDuration
      });
      currentOffset += pauseWidth;
      lastStart = time;
    }
  }

  if (lastStart && sorted.length > 0) {
    const lastClock = sorted[sorted.length - 1];
    const now = new Date();
    const duration = (now.getTime() - lastStart.getTime()) / 1000;
    const segmentWidth = (duration / expectedSecs) * 100;

    segments.push({
      type: lastClock.type === 'pause' ? 'pause' : 'work',
      width: Math.min(segmentWidth, 100 - currentOffset),
      left: currentOffset,
      duration,
      active: true
    });
  }

  return segments;
}

export function get_day_pause_secs(clocks: Clock[]): number {
  let totalPauseSecs = 0;
  if (clocks.length === 0) return totalPauseSecs;

  const now = new Date();
  const sorted = [...clocks]
    .filter(c => new Date(c.timestamp).getTime() <= now.getTime())
    .sort((a, b) => new Date(a.timestamp).getTime() - new Date(b.timestamp).getTime());
  if (sorted.length === 0) return totalPauseSecs;
  let lastPauseStart: Date | null = null;

  for (const clock of sorted) {
    const time = new Date(clock.timestamp);
    if (clock.type === 'pause') {
      lastPauseStart = time;
    } else if (clock.type === 'continue' && lastPauseStart) {
      totalPauseSecs += (time.getTime() - lastPauseStart.getTime()) / 1000;
      lastPauseStart = null;
    } else if (clock.type === 'out' && lastPauseStart) {
      totalPauseSecs += (time.getTime() - lastPauseStart.getTime()) / 1000;
      lastPauseStart = null;
    }
  }

  const last = sorted[sorted.length - 1];
  if (last.type === 'pause' && lastPauseStart) {
    totalPauseSecs += (now.getTime() - lastPauseStart.getTime()) / 1000;
  }

  return Math.round(totalPauseSecs);
}

export function get_today_secs(clocks: Clock[]): number {
  let totalSecs = 0;

  const now = new Date();

  // Filtrar solo clocks de hoy y que no estén en el futuro
  const todayClocks = filterTodayClocks(clocks)
    .filter(c => new Date(c.timestamp).getTime() <= now.getTime());

  if (todayClocks.length === 0) {
    return totalSecs;
  }

  todayClocks.sort((a, b) => new Date(a.timestamp).getTime() - new Date(b.timestamp).getTime());

  let last_clock = todayClocks[todayClocks.length - 1];

  // lastStart guarda el momento en que empezó a trabajar (in o continue)
  let lastStart: Date | null = null;

  for (const clock of todayClocks) {
    const time = new Date(clock.timestamp);

    // Si es inicio de trabajo (in o continue)
    if (clock.type === "in" || clock.type === "continue") {
      lastStart = time;
    }
    // Si es fin de trabajo (out o pause) y hay un inicio previo
    else if ((clock.type === "out" || clock.type === "pause") && lastStart) {
      const diff = (time.getTime() - lastStart.getTime()) / 1000;
      totalSecs += diff;
      lastStart = null;
    }
  }

  // Si el último clock de hoy es 'in' o 'continue', seguir contando hasta ahora
  if ((last_clock.type === 'in' || last_clock.type === 'continue') && lastStart) {
    totalSecs += (now.getTime() - lastStart.getTime()) / 1000;
  }

  return Math.round(totalSecs);
}

export function get_today_pause_secs(clocks: Clock[]): number {
  let totalPauseSecs = 0;

  if (clocks.length === 0) {
    return totalPauseSecs;
  }

  const now = new Date();
  const sorted = [...clocks]
    .filter(c => new Date(c.timestamp).getTime() <= now.getTime())
    .sort((a, b) => new Date(a.timestamp).getTime() - new Date(b.timestamp).getTime());

  if (sorted.length === 0) return totalPauseSecs;

  let last_clock = sorted[sorted.length - 1];

  // lastPauseStart guarda el momento en que empezó la pausa
  let lastPauseStart: Date | null = null;

  for (const clock of sorted) {
    const time = new Date(clock.timestamp);

    // Si es inicio de pausa
    if (clock.type === "pause") {
      lastPauseStart = time;
    }
    // Si es fin de pausa (continue) y hay una pausa previa
    else if (clock.type === "continue" && lastPauseStart) {
      const diff = (time.getTime() - lastPauseStart.getTime()) / 1000;
      totalPauseSecs += diff;
      lastPauseStart = null;
    }
    // Si es salida (out), también termina la pausa si estaba en pausa
    else if (clock.type === "out" && lastPauseStart) {
      const diff = (time.getTime() - lastPauseStart.getTime()) / 1000;
      totalPauseSecs += diff;
      lastPauseStart = null;
    }
  }

  // Si el último clock es 'pause', seguir contando hasta ahora
  if (last_clock.type === 'pause' && lastPauseStart) {
    totalPauseSecs += (now.getTime() - lastPauseStart.getTime()) / 1000;
  }

  return Math.round(totalPauseSecs);
}

/**
 * Obtiene el tipo del último fichaje en un array de clocks
 */
export function getLastClockType(clocks: Clock[]): 'in' | 'out' | null {
  if (clocks.length === 0) {
    return null;
  }

  const sortedClocks = [...clocks].sort(
    (a, b) => new Date(a.timestamp).getTime() - new Date(b.timestamp).getTime()
  );

  return sortedClocks[sortedClocks.length - 1].type as 'in' | 'out';
}

/**
 * Calcula el siguiente tipo de fichaje requerido basado en el último tipo
 * Retorna null si no hay restricción (cuando no hay fichajes previos)
 */
export function getNextRequiredClockType(clocks: Clock[]): 'in' | 'out' | null {
  const lastType = getLastClockType(clocks);

  if (lastType === null) {
    return null; // No hay restricción
  }

  return lastType === 'in' ? 'out' : 'in';
}

/**
 * Verifica si un clock tiene cambios pendientes de aprobación
 */
export function hasPendingChanges(clock: Clock): boolean {
  return clock.changes && clock.changes.some(change =>
    change.state === 'pendiente' || change.state === 'pending'
  );
}

/**
 * Formatea un timestamp para usarlo en un input HTML de tipo time (HH:MM)
 */
export function formatTimeForInput(timestamp: Date | string): string {
  const date = new Date(timestamp);
  return date.toTimeString().slice(0, 5);
}

/**
 * Obtiene la última justificación de un clock desde su historial de cambios
 */
export function getLastJustification(clock: Clock): string {
  if (clock.changes && clock.changes.length > 0) {
    const lastChange = clock.changes[clock.changes.length - 1];
    return lastChange.justification || '';
  }
  return '';
}

// ============================================
// Funciones de transformación de fecha/hora
// ============================================

/**
 * Interfaz para las partes de una fecha
 */
export interface DateParts {
  yyyy: string;
  mm: string;
  dd: string;
  hh: string;
  min: string;
}

/**
 * Separa una fecha en sus partes componentes (año, mes, día, hora, minutos)
 * Útil para inicializar formularios con date/time pickers
 */
export function formatDateTimeToParts(date: Date): DateParts {
  const yyyy = date.getFullYear().toString();
  const mm = String(date.getMonth() + 1).padStart(2, '0');
  const dd = String(date.getDate()).padStart(2, '0');
  const hh = String(date.getHours()).padStart(2, '0');
  const min = String(date.getMinutes()).padStart(2, '0');

  return { yyyy, mm, dd, hh, min };
}

/**
 * Formatea una fecha a string en formato "YYYY-MM-DD HH:MM"
 * Útil para mostrar fecha y hora juntas
 */
export function formatDateTimeToString(date: Date): string {
  const parts = formatDateTimeToParts(date);
  return `${parts.yyyy}-${parts.mm}-${parts.dd} ${parts.hh}:${parts.min}`;
}

/**
 * Formatea una fecha a string en formato "YYYY-MM-DD"
 * Útil para inputs de tipo date
 */
export function formatDateToString(date: Date): string {
  const parts = formatDateTimeToParts(date);
  return `${parts.yyyy}-${parts.mm}-${parts.dd}`;
}

/**
 * Formatea una fecha a string en formato "HH:MM"
 * Útil para inputs de tipo time
 */
export function formatTimeToString(date: Date): string {
  const parts = formatDateTimeToParts(date);
  return `${parts.hh}:${parts.min}`;
}

/**
 * Combina una fecha (string YYYY-MM-DD) y hora (string HH:MM) en un objeto Date
 */
export function combineDateAndTime(dateStr: string, timeStr: string): Date {
  return new Date(`${dateStr}T${timeStr}`);
}

